import { useDispatch, useSelector } from 'react-redux';
import {
    selectModularAddOnBenefits,
    selectModularFamily,
    selectModularPlans,
    selectModularPolicyId,
    selectModularSelectedBasePlan,
    selectModularUpgradedFamilyStructure
} from '../../../../redux/slices/ModularEnrolmentSlice/selectors';
import { ReduxState } from '../../../../redux';
import { familyDefinition, getDependentMappings, IMappings } from '../../../../utils/mapping';
import { useEffect, useMemo, useState } from 'react';
import { fetchModularFamily, resetCrudErrors, resetCrudFamily } from '../../../../redux/slices/ModularEnrolmentSlice';
import {
    anyOfChildOrSiblingOptions,
    IFamilyToDisplay,
    IModularValidationModal,
    IRelation,
    parentInLawsOptions
} from '../types';
import { IGender, IModularFamily } from '../../../../redux/slices/ModularEnrolmentSlice/types';
import { capitalizeFirstLetter, formatDateString, replaceFamilyStructureText } from '../../../../utils/common';
import { IDropdownItem, IForm } from '../../ManageModularMember/types';
import { initialFormValues } from '../utils';
import { Toast } from '@loophealth/loop-ui-web-library';
import { deleteMember as deleteModularMember, editMember as editModularMember, addMember } from '../../../../adapters';
import useSegment from '../../../../utils/hooks/useSegment';
import { SEGMENT_ACTIONS } from '../../../../utils/constants/SegmentActionConstants';
import {
    fetchModularAddOnBenefits,
    fetchModularAlaCarteBenefits
} from '../../../../redux/slices/ModularEnrolmentSlice/thunks';
import { FlagsmithFlags, useFeatureFlag } from '../../../../utils/flagsmith';
import { IError } from '../../../../redux/types';
import useModulerEnrolmentContext from '../../../pages/SPAContainer/pages/ModularEnrolmentPage/ModulerEnrolmentContext';

interface IAddDependents {
    currentMapping: IMappings | null;
    targetMapping: IMappings | null;
    getTitle: (index: number) => string;
    getMembersToDisplay: (category: string) => IModularFamily[];
    relations: string[];
    setSelectedParentOption: (value: string) => void;
    addMemberModal: boolean;
    setAddMemberModal: (value: boolean) => void;
    formValues: IForm;
    formErrors: IForm;
    onAddCardClick: (relationCategory: string) => void;
    handleFormChange: (e: React.ChangeEvent<HTMLInputElement> | Date | string, type: keyof IForm) => void;
    relationshipValues: IDropdownItem[];
    currentAction: IAction;
    editMemberModal: boolean;
    setEditMemberModal: (value: boolean) => void;
    onEditCardClick: (member: IModularFamily) => void;
    isLoading: boolean;
    onSaveDetails: (validateEdit?: boolean) => void;
    crudLoading: boolean;
    setDeleteMemberModal: (value: boolean) => void;
    deleteMemberModal: boolean;
    onDeleteMember: () => void;
    onDeleteClick: (id: string, relationCategory: string) => void;
    nonUpgradedRelations: string[];
    validationModal?: IModularValidationModal;
    setValidationModal: (value?: IModularValidationModal) => void;
}

export enum IAction {
    'ADD' = 'Add',
    'EDIT' = 'Edit',
    'DELETE' = 'Delete'
}

export const useAddDependents = (): IAddDependents => {
    const dispatch = useDispatch();
    const trackClick = useSegment('click');
    const trackPage = useSegment('page');
    const isFamilyUpdateEnabled = useFeatureFlag(FlagsmithFlags.FAMILY_ADD_ONS);
    const isPartnerSupportEnabled = useFeatureFlag(FlagsmithFlags.PARTNER_SUPPORT);
    const loopToast = Toast.useToast();
    const selectedPolicyId = useSelector(selectModularPolicyId);
    const selectedModularPolicyId = useSelector(selectModularSelectedBasePlan);
    const upgradedFamilyDefinition = useSelector(selectModularUpgradedFamilyStructure);
    const isAddOnsLoading = useSelector(selectModularAddOnBenefits)?.loading;
    const policyList = useSelector(selectModularPlans).data;
    const selectedPolicy = policyList?.find((policy) => policy.id === selectedModularPolicyId);
    const [familyStructure, setFamilyStructure] = useState(selectedPolicy?.familyDefinition || '');
    const userData = useSelector((state: ReduxState) => state.user?.userData?.data);
    const currentFamily = useSelector(selectModularFamily).data;
    const [currentMapping, setCurrentMapping] = useState<IMappings | null>(null);
    const [targetMapping, setTargetMapping] = useState<IMappings | null>(null);
    const [familyToDisplay, setFamilyToDisplay] = useState<IFamilyToDisplay | null>(null);
    const [relations, setRelations] = useState<string[]>([]);
    const [selectedParentOption, setSelectedParentOption] = useState<string>('');
    const [selectedRelationship, setSelectedRelationship] = useState<IRelation>('');
    const [addMemberModal, setAddMemberModal] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<IForm>(initialFormValues);
    const [formErrors, setFormErrors] = useState<IForm>(initialFormValues);
    const [currentAction, setCurrentAction] = useState<IAction>(IAction.ADD);
    const [selectedRelationCategory, setSelectedRelationCategory] = useState<string>('');
    const [relationshipValues, setRelationshipValues] = useState<IDropdownItem[]>([]);
    const [editMemberModal, setEditMemberModal] = useState<boolean>(false);
    const isLoading = useSelector(selectModularFamily).loading;
    const [crudLoading, setCrudLoading] = useState<boolean>(false);
    const [editMember, setEditMember] = useState<IModularFamily | null>(null);
    const [deleteMemberModal, setDeleteMemberModal] = useState<boolean>(false);
    const [deleteMember, setDeleteMember] = useState<string>('');
    const [validationModal, setValidationModal] = useState<IModularValidationModal>();
    const { setIsFamilyInfoUpdated } = useModulerEnrolmentContext();

    const nonUpgradedRelations = useMemo(() => selectedPolicy?.familyDefinition?.split(' + ') ?? [], [selectedPolicy]);

    useEffect(() => {
        if (userData.userId && selectedModularPolicyId) {
            dispatch(fetchModularFamily({ userId: userData.userId, policyId: selectedPolicyId }));
            if (isFamilyUpdateEnabled) {
                dispatch(
                    fetchModularAddOnBenefits({
                        userId: userData.userId,
                        policyId: selectedPolicyId,
                        benefitId: selectedModularPolicyId,
                        baseFamilyDefinition: selectedPolicy?.familyDefinition || ''
                    })
                );
            }
            trackPage(SEGMENT_ACTIONS.PAGE.ADD_DEPENDENT_PAGE);
        }
    }, [userData.userId, selectedModularPolicyId]);

    useEffect(() => {
        if (upgradedFamilyDefinition && isFamilyUpdateEnabled) {
            setFamilyStructure(upgradedFamilyDefinition);
        } else {
            setFamilyStructure(selectedPolicy?.familyDefinition || '');
        }
    }, [upgradedFamilyDefinition]);

    useEffect(() => {
        let dependent: IFamilyToDisplay = {};
        const spouseSelf = ['self', 'spouse'];
        if (isPartnerSupportEnabled) spouseSelf.push('partner');
        const children = currentFamily?.filter((rel) => rel.relationship.toLowerCase() === 'Child'.toLowerCase());
        const selfSpouse = currentFamily
            ?.filter((rel) => spouseSelf.includes(rel.relationship.toLowerCase()))
            .sort((a, b) => {
                if (a.relationship.toLowerCase() === 'self') return -1;
                if (b.relationship.toLowerCase() === 'self') return 1;
                return 0;
            });
        const parents = currentFamily?.filter((rel) => ['parent'].includes(rel.relationship.toLowerCase()));
        const parentsInLaw = currentFamily?.filter((rel) => ['parent-in-law'].includes(rel.relationship.toLowerCase()));
        const siblings = currentFamily?.filter((rel) => ['sibling'].includes(rel.relationship.toLowerCase()));
        dependent = { selfSpouse, children, parents, parentsInLaw, siblings };
        setFamilyToDisplay(dependent);

        setTargetMapping(familyDefinition[familyStructure]);
        const dependentTypes = familyStructure.split(' + ');
        setRelations(dependentTypes);

        currentFamily && setCurrentMapping(getDependentMappings(currentFamily, familyStructure));
    }, [currentFamily, familyStructure]);

    // handle relationship dropdown population
    useEffect((): void => {
        const relationships: IDropdownItem[] = [];
        if (selectedRelationCategory === 'Parents (including in-laws)') {
            relationships.push({ name: 'Parent', value: 'parent' }, { name: 'Parent-in-law', value: 'parent-in-law' });
            setRelationshipValues(relationships);
        } else if (anyOfChildOrSiblingOptions.includes(selectedRelationCategory)) {
            relationships.push({ name: 'Child', value: 'child' }, { name: 'Sibling', value: 'sibling' });
            setRelationshipValues(relationships);
        } else if (selectedRelationCategory === 'Spouse' && isPartnerSupportEnabled) {
            relationships.push({ name: 'Spouse', value: 'spouse' }, { name: 'Partner', value: 'partner' });
            setFormValues({ ...formValues, relationship: initialFormValues.relationship });
            setRelationshipValues(relationships);
        } else {
            relationships.push({
                name: capitalizeFirstLetter(selectedRelationship),
                value: selectedRelationship.toLowerCase()
            });
            setRelationshipValues(relationships);
            setFormValues({ ...formValues, relationship: selectedRelationship.toLowerCase() });
        }
    }, [selectedRelationCategory, selectedRelationship]);

    useEffect(() => {
        if (!addMemberModal && !editMemberModal && !deleteMemberModal) {
            dispatch(resetCrudErrors());
        }
    }, [addMemberModal, editMemberModal, deleteMemberModal]);

    const getTitle = (index: number): string => {
        let title: string;
        if (relations.length === 1 || (relations[index] === 'Self' && index === 0)) {
            if (relations[index] === 'Parents Only') title = 'Parents';
            else title = 'Your details';
        } else if (relations.length === 2 || index === 1) {
            // For Self + Spouse title
            if (relations[index].includes('Sibling')) title = 'Your Siblings';
            else if (relations[index].includes('Parents')) title = 'Parents';
            else title = 'You and Your Spouse';
            return title;
        } else if (relations.length > 4 && index === 3) {
            // For Parents + Parents-in-law title
            title = `${relations[3]} + ${relations[4]}`;
        } else if (index === 2) {
            // For Children
            const childCount = targetMapping?.dependents?.[index - 1]?.number || 0;
            if (relations[index].includes('(including siblings)')) title = `${childCount} Children or Siblings`;
            else title = `${childCount} Children`;
        } else {
            const numberOfDependants = targetMapping?.dependents?.[index - 1]?.number || 0;
            if (relations[index].includes('Sibling')) {
                // For Siblings
                title = `${numberOfDependants} Sibling${numberOfDependants > 1 ? 's' : ''}`;
            } else {
                // For Parents
                title = numberOfDependants ? numberOfDependants + ' ' + relations[index] : relations[index];
            }
        }
        return replaceFamilyStructureText(title);
    };

    const getMembersToDisplay = (category: string): IModularFamily[] => {
        const children = ['1 Kid', '2 Kids', '3 Kids', '4 Kids', '5 Kids', '6 Kids'];
        const siblings = ['2 Siblings'];
        const oneOfParentsOrInLawsOptions = ['Parents OR parents-in-law', 'Parents OR parent-in-laws'];
        const anyOfParentsOrInLawsOptions = ['Parents (including in-laws)'];

        if (category === 'Spouse' || category === 'Self') return familyToDisplay?.selfSpouse || [];
        else if (children.includes(category)) return familyToDisplay?.children || [];
        else if (siblings.includes(category)) return familyToDisplay?.siblings || [];
        else if (category === 'Parents' || category === 'Parents Only') return familyToDisplay?.parents || [];
        else if (parentInLawsOptions.includes(category)) return familyToDisplay?.parentsInLaw || [];
        else if (oneOfParentsOrInLawsOptions.includes(category)) {
            if (familyToDisplay?.parents && familyToDisplay?.parents?.length > 0) {
                return familyToDisplay?.parents || [];
            }
            return familyToDisplay?.parentsInLaw || [];
        } else if (anyOfParentsOrInLawsOptions.includes(category)) {
            return [...(familyToDisplay?.parents ?? []), ...(familyToDisplay?.parentsInLaw ?? [])];
        } else if (anyOfChildOrSiblingOptions.includes(category)) {
            return [...(familyToDisplay?.children ?? []), ...(familyToDisplay?.siblings ?? [])];
        } else return [];
    };

    const getRelation = (relationship: string): IRelation => {
        switch (relationship) {
        case 'Spouse':
            return 'Spouse';
        case '1 Kid':
        case '2 Kids':
        case '3 Kids':
        case '4 Kids':
        case '5 Kids':
        case '6 Kids':
            return 'Child';
        case 'Parents':
        case 'Parents Only':
            return 'Parent';
        case 'Siblings':
        case '2 Siblings':
            return 'Sibling';
        case 'Parent-in-laws':
        case 'Parents-in-law':
        case '1 Parent-in-law':
            return 'Parent-in-law';
        case 'Parents OR parent-in-laws':
        case 'Parents OR parents-in-law':
            return selectedParentOption === 'Parent' ? 'Parent' : 'Parent-in-law';
        default:
            return relationship as IRelation;
        }
    };

    const onAddCardClick = (relationCategory: string) => {
        const clickAction = SEGMENT_ACTIONS.CLICK.ADD_DEPENDENT;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                dependent_type: getRelation(relationCategory),
                eligible_members: familyStructure
            }
        });
        setSelectedRelationship(getRelation(relationCategory));
        dispatch(resetCrudFamily());
        setFormValues(initialFormValues);
        setFormErrors(initialFormValues);
        setCurrentAction(IAction.ADD);
        setSelectedRelationCategory('');
        setTimeout(() => {
            setSelectedRelationCategory(relationCategory);
        }, 0);
        setAddMemberModal(true);
    };

    const onEditCardClick = (member: IModularFamily) => {
        dispatch(resetCrudFamily());
        setEditMember(member);
        setCurrentAction(IAction.EDIT);
        setEditMemberModal(true);
        setSelectedRelationship(member.relationship as IRelation);
        setFormValues({
            firstName: member.firstName,
            lastName: member.lastName,
            dob: member.dob,
            relationship: member.relationship,
            gender: member.gender,
            mobile: member.mobile
        });
        setFormErrors(initialFormValues);
        setSelectedRelationCategory(member.relationship);
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement> | Date | string, type: keyof IForm) => {
        switch (type) {
        case 'firstName':
        case 'lastName':
        case 'mobile':
            const value = (e as React.ChangeEvent<HTMLInputElement>).target.value;
            setFormValues({ ...formValues, [type]: value });
            setFormErrors({ ...formErrors, [type]: '' });
            break;
        case 'dob':
            const date = new Date(e as Date).toISOString();
            setFormValues({ ...formValues, dob: date });
            setFormErrors({ ...formErrors, dob: '' });
            break;
        case 'relationship':
        case 'gender':
            const data = e as string;
            setFormValues({ ...formValues, [type]: data });
            setFormErrors({ ...formErrors, [type]: '' });
            break;
        }
    };

    const isFormValid = (): boolean => {
        const errors: IForm = { ...initialFormValues };
        if (!formValues.firstName) errors.firstName = 'First name is required';
        if (!formValues.dob) errors.dob = 'Date of birth is required';
        if (!formValues.relationship) errors.relationship = 'Relationship is required';
        if (!formValues.gender) errors.gender = 'Gender is required';
        if (formValues.mobile && formValues.mobile.length !== 10) errors.mobile = 'Enter valid mobile number';
        if (Object.values(errors).some((value) => typeof value === 'string' && value.trim() !== '')) {
            const pageAction = SEGMENT_ACTIONS.PAGE.ERROR;
            trackPage({
                name: pageAction.name,
                properties: {
                    ...pageAction.properties,
                    errors
                }
            });
            setFormErrors(errors);
            return false;
        }
        return true;
    };

    const onSaveDetails = async (validateEdit = true) => {
        if (crudLoading || !isFormValid()) return;

        try {
            const memberDetails = {
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                dob: formatDateString(formValues.dob),
                relationship: formValues.relationship,
                mobile: formValues.mobile,
                gender: formValues.gender as IGender,
                employer: userData.employer || '',
                employeeId: userData.employeeId || ''
            };
            const clickAction = SEGMENT_ACTIONS.CLICK.SAVE_DETAILS;
            trackClick({
                name: clickAction.name,
                properties: {
                    ...clickAction.properties,
                    ...memberDetails
                }
            });
            setCrudLoading(true);
            if (currentAction === IAction.ADD) {
                await addMember(userData.userId, selectedPolicyId, memberDetails);
                setAddMemberModal(false);
                loopToast?.success(`${capitalizeFirstLetter(memberDetails.relationship)} added successfully`);
            } else {
                const response = await editModularMember(
                    userData.userId,
                    selectedPolicyId,
                    editMember?.id || '',
                    memberDetails,
                    validateEdit ? 'validate' : 'commit'
                );
                if (validateEdit) {
                    if (response.isValid) {
                        return onSaveDetails(false);
                    } else {
                        setEditMemberModal(false);
                        setCrudLoading(false);
                        return setValidationModal({
                            loading: false,
                            relationship: capitalizeFirstLetter(memberDetails.relationship),
                            mode: 'update',
                            invalidBenefits: response.invalidBenefits || []
                        });
                    }
                } else if (validationModal) {
                    dispatch(
                        fetchModularAlaCarteBenefits({
                            userId: userData.userId,
                            policyId: selectedPolicyId,
                            benefitId: selectedModularPolicyId
                        })
                    );
                    setValidationModal(undefined);
                }
                setEditMemberModal(false);
                loopToast?.success(`${capitalizeFirstLetter(memberDetails.relationship)} details edited successfully`);
            }
            setCrudLoading(false);
            dispatch(fetchModularFamily({ userId: userData.userId, policyId: selectedPolicyId }));
            setFormErrors(initialFormValues);
            setIsFamilyInfoUpdated(true);
        } catch (error) {
            const err = JSON.parse((error as Error).message);
            const errors: IForm = { ...initialFormValues };
            Object.keys(err).forEach((key) => {
                if (key in errors) errors[key as keyof IForm] = err?.[key];
                else if (key === 'other') loopToast?.error(err?.[key]);
            });
            const pageAction = SEGMENT_ACTIONS.PAGE.ERROR;
            trackPage({
                name: pageAction.name,
                properties: {
                    ...pageAction.properties,
                    errors
                }
            });
            setFormErrors(errors);
            setCrudLoading(false);
        }
    };

    const onDeleteClick = async (id: string, relationCategory: string) => {
        setValidationModal({ loading: true });
        try {
            const memberToDelete = currentFamily?.find((member) => member.id === id);
            const response = await deleteModularMember(userData.userId, selectedPolicyId, id, 'validate');
            if (response.isValid) {
                setValidationModal(undefined);
                setSelectedRelationship(getRelation(relationCategory));
                setCurrentAction(IAction.DELETE);
                dispatch(resetCrudFamily());
                setDeleteMember(id);
                setDeleteMemberModal(true);
            } else {
                setCurrentAction(IAction.DELETE);
                setDeleteMember(id);
                setValidationModal({
                    loading: false,
                    relationship: capitalizeFirstLetter(memberToDelete?.relationship ?? ''),
                    mode: 'delete',
                    invalidBenefits: response.invalidBenefits || []
                });
            }
        } catch (error) {
            loopToast?.error((error as IError).message, 'Error deleting member');
            setValidationModal(undefined);
        }
    };

    const onDeleteMember = async () => {
        if (crudLoading) return;
        try {
            setCrudLoading(true);
            const memberToDelete = currentFamily?.find((member) => member.id === deleteMember);
            const response = await deleteModularMember(userData.userId, selectedPolicyId, deleteMember);
            dispatch(fetchModularFamily({ userId: userData.userId, policyId: selectedPolicyId || '' }));
            if (response) {
                loopToast?.success(
                    `${capitalizeFirstLetter(
                        memberToDelete?.relationship ?? selectedRelationship
                    )} removed successfully`
                );
            }
            if (validationModal) {
                dispatch(
                    fetchModularAlaCarteBenefits({
                        userId: userData.userId,
                        policyId: selectedPolicyId,
                        benefitId: selectedModularPolicyId
                    })
                );
                setValidationModal(undefined);
            }
            setDeleteMemberModal(false);
            setCrudLoading(false);
            setIsFamilyInfoUpdated(true);
        } catch (error) {
            const err = JSON.parse((error as Error).message);
            loopToast?.error(err.message);
            setDeleteMemberModal(false);
            setCrudLoading(false);
        }
    };

    return {
        currentMapping,
        getTitle,
        getMembersToDisplay,
        relations,
        targetMapping,
        setSelectedParentOption,
        addMemberModal,
        setAddMemberModal,
        formValues,
        formErrors,
        onAddCardClick,
        handleFormChange,
        relationshipValues,
        currentAction,
        editMemberModal,
        setEditMemberModal,
        onEditCardClick,
        isLoading: isAddOnsLoading || isLoading,
        onSaveDetails,
        crudLoading,
        setDeleteMemberModal,
        deleteMemberModal,
        onDeleteMember,
        onDeleteClick,
        nonUpgradedRelations,
        validationModal,
        setValidationModal
    };
};
